import { Button, Container, Section } from '@resolute/ui';

import { useCookieConsent } from './cookie-consent-context';

import { Reveal, StaggerGroup, StaggerItem } from '@/components/motion/reveal';
import { PageHero } from '@/components/page-hero';
import { CONTACT_DETAILS } from '@/features/contact/contact-details';
import { Seo } from '@/lib/seo';

const CATEGORIES = [
  {
    title: 'Necessary',
    status: 'Always on',
    body: 'Required for core features to work — your wishlist, compare list, and remembering this cookie preference itself. Stored entirely in your browser (localStorage), never sent to our servers.',
  },
  {
    title: 'Analytics',
    status: 'Off by default',
    body: 'Would help us understand how the site is used so we can improve it. No analytics currently run on this site — this category exists so your preference is ready ahead of any future analytics integration, and you can opt in or out at any time.',
  },
  {
    title: 'Marketing',
    status: 'Off by default',
    body: 'Not currently used on this site. Reserved for possible future ad-personalisation features — off by default and will stay off unless you explicitly opt in.',
  },
];

export default function CookiesPage() {
  const { status, openPreferences } = useCookieConsent();

  return (
    <>
      <Seo
        title="Cookie Policy"
        description="How Resolute Property Solutions uses cookies and local storage, and how to manage your preferences."
        path="/cookies"
      />

      <PageHero
        eyebrow="Legal"
        title="Cookie Policy."
        titleLine2="What we store, and why."
        description="This site doesn't set third-party tracking cookies by default. Here's exactly what we store, why, and how to change your preference at any time."
      />

      <Section>
        <Container className="max-w-3xl">
          <Reveal>
            <h2 className="text-lg font-semibold">How we use cookies and local storage</h2>
            <p className="mt-2 leading-relaxed text-muted-foreground">
              Features like your Wishlist, Compare list and recent searches are saved using your
              browser&apos;s local storage, not traditional cookies — they stay on your device and
              are never transmitted to our servers. The one exception is this cookie preference
              itself, which we also store locally so we don&apos;t ask you again on every visit.
            </p>
          </Reveal>

          <StaggerGroup className="mt-8 space-y-4">
            {CATEGORIES.map((c) => (
              <StaggerItem key={c.title} className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-medium">{c.title}</h3>
                  <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {c.status}
                  </span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <Reveal className="mt-10 rounded-lg border border-border bg-card p-6">
            <h2 className="text-lg font-semibold">Manage your preferences</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {status === null
                ? "You haven't made a choice yet — it'll appear the next time you load a page, or you can set it now."
                : 'You can revisit and change your choice at any time.'}
            </p>
            <Button variant="outline" size="sm" className="mt-4" onClick={openPreferences}>
              {status === null ? 'Set cookie preferences' : 'Change cookie preferences'}
            </Button>
          </Reveal>

          <Reveal className="mt-8">
            <h2 className="text-lg font-semibold">Questions?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Email us at{' '}
              <a href={`mailto:${CONTACT_DETAILS.email}`} className="font-medium text-primary hover:underline">
                {CONTACT_DETAILS.email}
              </a>{' '}
              and we&apos;ll help.
            </p>
          </Reveal>
        </Container>
      </Section>
    </>
  );
}
